import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";

import {
  createGating,
  updateGating,
  deleteGating,
  updateGatingSettings,
  invalidateGatingState,
  cloneGating,
  fetchGatingSucceeded,
} from "actions/gating";

import _ from "lodash";
import cx from "classnames";

import {
  Popover,
  OverlayTrigger,
  Form,
  Button,
  Image,
  Modal
} from "react-bootstrap";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import storyboardRoleService from "services/storyboardRoles.service";

import {
  GATING_WIDGETS,
  SPATIAL_GATING_WIDGETS
} from "constants/gatingWidgetsRequest";
import { IKImage } from "imagekitio-react";
import { ContentGating } from "pages/storyboards/storyboardSettings/websiteSettings/gating";
import { createGatingConfig, fetchStoryboard, resetGatingConfig, updateGatingConfig, updateStoryboard } from "actions/storyboard";
import shortid from "shortid";
import { createMixpanelEvent } from "actions/mixpanel";
import { getStoryboardMixpanelEventPayload } from "services/mixpanelUtil";
import { MIXPANEL_GATING_CONFIGURED_FROM_EDITOR } from "constants/mixpanelEvents";
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";

import { getBaseUrl } from "services/util";

import { ShortLoader } from "components/ApplicationLoader";
import { SLIDE_STORYBOARDS } from "constants/storyboardType";
import styles from "./settings.module.scss";

export const GatingSettingsPage = (
  { open, style, onClose, editorSession, defaultParent, storyboardType, editorType, target, ...rest }
) => {
  const history = useHistory();
  const { id } = useParams();
  const { url } = useRouteMatch();

  const storyboard = useSelector((state) =>
    _.get(state, "storyboards.storyboard.item", null)
  );
  const fetched = useSelector((state) => _.get(state, "gatings.fetched", false));
  const gatings = useSelector((state) => _.get(state, "gatings.items", []));
  const deletion = useSelector((state) => _.get(state, "gatings.deletion", {}));
  const creating = useSelector((state) =>
    _.get(state, "gatings.create.creating", false)
  );
  const customFormFields = useSelector((state) =>
    _.get(state, "formFields.items", null)
  );
  const loading = useSelector((state) => _.get(state, `storyboards.storyboard.loading`, false));
  const updating = useSelector((state) => _.get(state, `storyboards.storyboard.updating`, false));
  const gatingConfigs = useSelector((state) => _.get(state, "storyboards.storyboard.item.gatingConfigurations", [])) || [];
  const initialGatingConfigsRef = useRef(null);
  const error = useSelector((state) => _.get(state, "storyboards.storyboard.error", null));

  const gatingsRef = useRef([]);

  let baseUrl = getBaseUrl(url);

  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);

  const mixpanel = _.get(storyboard, 'mixpanel', []);
  const gatingId = _.get(storyboard, 'gatingId', []);
  const triggerTypeMapping = {
    INTERACTIVE_SECTION: "Sections",
    INTERACTIVE_DOWNLOAD: "On Download",
    TIME: "Time",
    PERCENTAGE: "Percentage"
  };

  useEffect(() => {
    if(error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to update storyboard`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      return;
    }
  }, [error]);

  useEffect(() => {
    if(!loading && initialGatingConfigsRef.current === null) {
      initialGatingConfigsRef.current = gatingConfigs;
    }
  }, [loading]);

  useEffect(() => {
    if (!Array.isArray(gatings) || gatings.length === 0) return;
  
    const hasChanged = !_.isEqual(gatingsRef.current, gatings);
  
    if (hasChanged) {
      gatings.forEach((gating) => {
        if (gating.id !== id) {
          dispatch(fetchGatingSucceeded(gating.id, gating));
        }
      });
  
      gatingsRef.current = gatings;
    }
  }, [gatings, id, dispatch]);

  useEffect(() => {
    if (deletion && deletion.deleted && _.get(deletion, "id", null) === id) {
      var nextGating = gatings[Math.max(_.get(deletion, "deletedIndex", 0), 0)];
      if (nextGating) {
        history.push(`${baseUrl}/gating/${nextGating.id}`);
      }

      dispatch(invalidateGatingState());
    }
  }, [deletion]);

  const updateWidgets = (gatingWidgets, customFormFields) => {
    let fields = customFormFields.filter((field) => !field.userDefined);
    let updatedArray = _.cloneDeep(gatingWidgets);

    _.forEach(updatedArray, (widget) => {
      let matchingWidget = _.find(fields, { name: widget.name });
      if (matchingWidget) {
        _.set(widget, "content", matchingWidget.id);
      }
    });

    return updatedArray;
  };

  const createNewGating = () => {
    const gatingWidget =
      SLIDE_STORYBOARDS.includes(storyboard.type)  ? SPATIAL_GATING_WIDGETS : GATING_WIDGETS;
    
    const widgetsWithNewUids = generateNewUids(gatingWidget);
    const updatedWidgets = updateWidgets(widgetsWithNewUids, customFormFields);
  
    if (!creating) {
      var createRequest = {
        name: "Untitled Gating",
        widgets: updatedWidgets,
        storyboard: storyboard.id,
      };

      dispatch(
        createGating(createRequest, (response) => {
          history.push(`${baseUrl}/gating/${response.id}`);
        })
      );
    }
  };

  const generateNewUids = (widgets) => {
    const uidMap = {};
    widgets.forEach((widget) => {
      const oldUid = widget.uid;
      uidMap[oldUid] = `widget_${shortid.generate()}`;
    });
  
    return widgets.map((widget) => {
      const newWidget = _.cloneDeep(widget);
      newWidget.uid = uidMap[widget.uid];
      
      if (widget.parent && uidMap[widget.parent]) {
        newWidget.parent = uidMap[widget.parent];
      }
      
      return newWidget;
    });
  };

  const handleGatingClick = (gating) => {
    history.push(`${baseUrl}/gating/${gating.id}`);
  };

  const handleSave = () => {
    const gatingConfigurations = _.get(storyboard, 'gatingConfigurations', []);

    let errorMessage = '';

    if (Array.isArray(gatingConfigurations)) {
      gatingConfigurations.forEach((item) => {
        if(errorMessage) return;

        if(item.enabled && (!item.gating || (item.autoGating && !item.conversionType))) {
          errorMessage = `A gating screen is required. Please select one.`;
          return;
        }

        if ((!item.id || (mixpanel && Object.keys(mixpanel).length > 0 && item.uid === gatingId)) && item.enabled &&
          !(
            (item.trigger?.value === null || item.trigger?.value === "") &&
            (item.trigger?.type === "INTERACTIVE_SECTION" || item.trigger?.type === "TIME")
          )) {
            const smartGating = _.get(item, "smart", false)
            const mixpanelPayload = getStoryboardMixpanelEventPayload(
            storyboard.id,
            storyboard.name,
            storyboard.type
          );
          const updatedPayload = {
            ...mixpanelPayload,
            gating_type: "Page",
            trigger: smartGating? "Smart Gating" : triggerTypeMapping[_.get(item, "trigger.type", "")] || _.get(item, "trigger.type", ""),
            optional: _.get(item, 'optional', ''),
          };
          dispatch(createMixpanelEvent(MIXPANEL_GATING_CONFIGURED_FROM_EDITOR, updatedPayload));
        }
      });
    }

    if(errorMessage) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={errorMessage}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
      return;
    }

    dispatch(
      updateStoryboard(storyboard.id, {
        gatingConfigurations
      }, () => {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`${storyboard.name} saved successfully`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        initialGatingConfigsRef.current = gatingConfigs;
      })
    );
  }

  const gatingConfiguration = useMemo(() => gatingConfigs.flatMap((data) => (data.uid && data.gating) ? data : []), [gatingConfigs]);

  const gatingsLength = gatings.length;

  return (
    <div className={styles.smartGatingSettings}>
      <div className={cx(styles.smartGatingFormWrapper, {
        [styles.smartGatingEmptyFormWrapper]: gatings.length < 1,
        [styles.smartGatingSpatialFormWrapper]: SLIDE_STORYBOARDS.includes(storyboardType)
      })}>
        <div className={styles.smartGatingFormTitleWrap}>
          <p className={styles.smartGatingFormTitle}>GATING SCREENS</p>
          <Button
            variant="primary"
            onClick={createNewGating}
            disabled={creating}
            className={styles.createGatingButton}
          >
            <i className={cx("cleverstory-plus", styles.addIcon)} />
            {!creating ? `Add Gating Screen` : `Adding...`}
          </Button>
        </div>
        <div className={styles.smartGatingForms}>
          <div className={styles.smartGatings}>
            {gatings.length > 0 ? (
              gatings.map((item) => {
                return (
                  <GatingCardView
                    key={item.id}
                    gating={item}
                    selected={item.id === id}
                    onClick={handleGatingClick}
                    gatingsLength={gatingsLength}
                    baseUrl={baseUrl}
                    storyboard={storyboard}
                    gatingConfig={gatingConfiguration}
                  />
                );
              })
            ) : (
              <div className={styles.emptySmartGatingContainer}>
                <div className={styles.emptyMessage}>
                  {loading ?
                    <ShortLoader className={styles.emptyloaderContainer}/> :
                      <>
                        <div className={styles.errorMessageContent}>
                          <div className={styles.errorMessageTitle}>
                            <h2 className={cx(styles.heading, styles.gatingHeading)}>Weather forecast</h2>
                          </div>
                          <div className={cx(styles.errorMessageSubText, styles.gatingErrorMessageSubText)}>
                            <span>
                              Clear skies and <br /> empty screens
                            </span>
                          </div>
                        </div>
                        <img
                          className={cx(styles.emptySmartGatingImage )}
                          src=""
                          alt="emptyMedia_image"
                        />
                      </>
                  }
                </div>
              </div>
            )}
          </div>
        </div>
        <Button
          variant="primary"
          disabled={!fetched || gatings.length === 0}
          onClick={() => {
            if(!loading) initialGatingConfigsRef.current = gatingConfigs;
            setOpenModal(true);
          }}
          className={cx(styles.manageTriggers)}
          data-track-event="Mixpanel" data-track-props="Manage Trigger from Editor" 
        >
          Manage Triggers
        </Button>
      </div>
      {openModal && (
        <GatingTrigger
          handleSave={handleSave}
          storyboard={storyboard}
          setOpenModal={setOpenModal}
          gatingConfigs={gatingConfigs}
          initialGatingConfigsRef={initialGatingConfigsRef}
        />
      )}
    </div>
  );
};

const GatingCardView = ({ gating, selected, onClick, gatingsLength, storyboard, baseUrl, gatingConfig }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const id = gating?.id || "";
  
  const updating = useSelector((state) =>
    _.get(state, `gatings.gating.${id}.update.updating`, false)
  );
  const success = useSelector((state) =>
    _.get(state, `gatings.gating.${id}.update.success`, false)
  );
  const deletingId = useSelector((state) =>
    _.get(state, `gatings.deletion.id`, false)
  );
  const isDeleting = deletingId === id;

  const progressive = gating?.progressive || false;
  const iconUrl = _.get(gating, "icon.url", '');

  const [editEnabled, setEditEnabled] = useState(false);
  const [name, setName] = useState(gating.name || "Untitled Gating");

  const titleActionRef = useRef();

  const inputRef = useCallback((node) => {
    if(!node) return;

    if(editEnabled) {
      node.focus();
    }
  }, [editEnabled]);

  useEffect(() => {
    if(!success || updating || selected) return;

    toast(
      <SuccessToast
        heading={`Success`}
        description={`${name} saved successfully`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );

    dispatch(invalidateGatingState(id));
  }, [success, updating, selected]);

  useEffect(() => {
    const clickHandler = (e) => {
      if(!editEnabled || titleActionRef.current.contains(e.target)) return;

      setEditEnabled(false);
      setName(gating.name || "Untitled Gating");
    };

    document.body.addEventListener("click", clickHandler);
    return () => document.body.removeEventListener("click", clickHandler);
  }, [editEnabled]);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    document.body.click();
    if (gatingsLength < 1) {
      return;
    }

    dispatch(deleteGating(gating.id, () => {
      gatingConfig.map(({uid, gating: data}) => {
        if(gating.id !== data.id) return;
  
        dispatch(updateGatingConfig(uid, {
          gating: null
        }));
      })
    }));
  };

  const handleCloneClick = (e) => {
    e.stopPropagation();
    document.body.click();

    dispatch(
      cloneGating(gating.id, (response) => {
        history.push(`${baseUrl}/gating/${response.id}`);
      })
    );
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    document.body.click();
    setEditEnabled(true);
  };

  const saveRename = () => {
    if(name.trim() === "") {
      setEditEnabled(false);
      setName(gating.name || "Untitled Gating");
      return;
    }

    dispatch(
      updateGatingSettings(id, {
        name,
      })
    );

    dispatch(updateGating(id));

    gatingConfig.map(({uid, gating: data}) => {
      if(gating.id !== data.id) return;

      dispatch(updateGatingConfig(uid, {
        gating: {
          ...data,
          name
        }
      }));
    });

    setEditEnabled(false);
  }

  const handleOverlay = (e) => {
    document.body.click();
  };

  const handleRenameClose = () => {
    setEditEnabled(false);
    setName(gating.name || "Untitled Gating");
  };

  const popover = (
    <Popover bsPrefix="popover" className={styles.pageSettingsPopover}>
      <Popover.Body className={styles.pageSettingsPopoverBody}>
        <div className={styles.pageSettingsPopoverContent}>
          <span onClick={handleCloneClick}>Clone</span>
          <span onClick={handleRenameClick}>Rename</span>
          {gatingsLength > 1 &&
            storyboardRoleService.canManageStoryboard(storyboard) && (
              <span onClick={handleDeleteClick}>Delete</span>
            )}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <div className={cx(styles.gatingFormCard, {[styles.gatingFormActiveCard]: selected})}>
      <div onClick={() => onClick(gating)} className={cx(styles.banner)}>
        <div className={cx(styles.iconContainer, {[styles.progressiveIconContainer]: progressive})}>
          {iconUrl ? 
            <IKImage
              className={styles.icon}
              path={iconUrl}
            /> : 
            <img src={'/images/storyboard.png'} className={cx(styles.icon, { [styles.default]: true })} />
          }
          {progressive && <span></span>}
        </div>
        {progressive && 
          <p className={styles.progressiveBtn}>
            <span>PROGRESSIVE</span>
          </p>
        }
      </div>
      <div className={styles.titleAction} ref={titleActionRef}>
        {
          editEnabled ? 
            <>
              <input 
                ref={inputRef} 
                name="gating-name" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
              />
              <div className={styles.actionGroup}>
                <button className={styles.action} disabled={updating} onClick={saveRename}>
                  <i className="cleverstory-tick" />
                </button>
                <button className={styles.action} disabled={updating} onClick={handleRenameClose}>
                  <i className="cleverstory-close" />
                </button>
              </div>
            </>
          :
          <>
            <span className={styles.name}>
              <TruncatedTextWithTooltip content={name} maxWidth={200} placement='top'/>
            </span>
            <OverlayTrigger
              rootClose={true}
              rootCloseEvent="click"
              trigger="click"
              placement="right"
              overlay={popover}
            >
              <i
                className={cx("cleverstory-more", styles.iconMore)}
                onClick={handleOverlay}
              />
            </OverlayTrigger>
          </>
        }
      </div>
      {isDeleting &&
        <div className={styles.gatingDeletion}>
          <p>Deleting...</p>
        </div>
      }
    </div>
  );
};

const GatingTrigger = ({
  storyboard,
  setOpenModal,
  initialGatingConfigsRef,
  gatingConfigs,
  handleSave
}) => {
  const updating = useSelector(state => _.get(state, "storyboards.storyboard.updating", false));
  const loading = useSelector(state => _.get(state, "storyboards.storyboard.loading", false));

  const dispatch = useDispatch();

  const createNewGating = () => {
    var request = {
      uid: shortid(),
      name: 'Untitled Gating',
      enabled: false,
      optional: false,
      trigger: { type: 'TIME', value: 10 },
      target: null
    }

    dispatch(createGatingConfig(request));
  }

  const handleResetClick = () => {
    dispatch(fetchStoryboard(storyboard.id));
  };

  const handleClose = () => {
    dispatch(resetGatingConfig(initialGatingConfigsRef.current));
    setOpenModal(false);
  }

  const isChanged = useMemo(() => _.isEqual(initialGatingConfigsRef.current, gatingConfigs));

  return (
    <Modal
      show={true}
      className={cx(styles.gatingTriggerContainer)}
      backdropClassName={styles.backdrop}
      centered
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Gating Triggers</Modal.Title>
        <div className={styles.modalButtonWrap}>
          <Button
            disabled={!storyboardRoleService.canEditStoryboard(storyboard)}
            variant="primary"
            onClick={createNewGating}
            className={styles.createGatingButton}
          >
            <i className={cx("cleverstory-plus", styles.addIcon)} />
            New Trigger
          </Button>
          <Button
            variant="info"
            onClick={handleClose}
            className={styles.closeButton}
          >
            <i className={cx("cleverstory-close", styles.closeButtonIcon)} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <ContentGating isGatingSettings storyboard={storyboard} gatingInEditor={true}/>
      </Modal.Body>
      {storyboardRoleService.canEditStoryboard(storyboard) && (
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="secondary"
            disabled={updating || loading || isChanged}
            className={styles.resetButton}
            type="reset"
            onClick={handleResetClick}
          >
            Reset
          </Button>
          <Button
            variant="primary"
            disabled={updating || loading || isChanged}
            className={styles.saveButton}
            type="submit"
            onClick={handleSave}
          >
            {updating ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};
